import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { useTenant } from '../components/ContextTenant.tsx';
import CreateTenantDialog from '../components/CreateTenantDialog.tsx';
import InviteDialog from '../components/InviteDialog.tsx';
import { useOverview } from '../hooks/useOverview.ts';

const formatDate = (value?: string | null) => (value ? dayjs(value).format('DD MMM YYYY, HH:mm') : 'No timestamp');

const statusChipColor = (status?: string | null): 'default' | 'success' | 'warning' | 'error' => {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'ACTIVE' || normalized === 'HEALTHY') {
    return 'success';
  }
  if (normalized === 'PENDING') {
    return 'warning';
  }
  if (normalized === 'DEGRADED' || normalized === 'SUSPENDED' || normalized === 'ERROR') {
    return 'error';
  }
  return 'default';
};

interface MetricTileProps {
  label: string;
  value: string;
  meta: string;
  tone?: 'default' | 'success' | 'warning' | 'error';
}

const MetricTile: React.FC<MetricTileProps> = ({ label, value, meta, tone = 'default' }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.25,
      borderRadius: 4,
      border: '1px solid',
      borderColor: tone === 'error'
        ? alpha('#b4443b', 0.18)
        : tone === 'warning'
          ? alpha('#a66a2c', 0.18)
          : tone === 'success'
            ? alpha('#2f6f57', 0.18)
            : 'divider',
      bgcolor: tone === 'error'
        ? alpha('#b4443b', 0.04)
        : tone === 'warning'
          ? alpha('#a66a2c', 0.05)
          : tone === 'success'
            ? alpha('#2f6f57', 0.05)
            : 'background.paper',
    }}
  >
    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.16em' }}>
      {label}
    </Typography>
    <Typography variant="h4" sx={{ mt: 0.5 }}>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      {meta}
    </Typography>
  </Paper>
);

interface PreviewPanelProps {
  title: string;
  subtitle: string;
  actionLabel?: string;
  actionTo?: string;
  children: React.ReactNode;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({ title, subtitle, actionLabel, actionTo, children }) => (
  <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
    <Stack
      direction="row"
      spacing={2}
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{ px: 2.5, py: 2.25 }}
    >
      <Box>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      </Box>
      {actionLabel && actionTo ? (
        <Button component={Link} to={actionTo} variant="text" size="small">
          {actionLabel}
        </Button>
      ) : null}
    </Stack>
    <Divider />
    <Box sx={{ px: 2.5, py: 1.5 }}>
      {children}
    </Box>
  </Paper>
);

const Dashboard: React.FC = () => {
  const { tenantId } = useTenant();
  const { data, loading, error, refresh } = useOverview();
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const headlineMeta = useMemo(() => {
    if (!data) {
      return 'Loading shared system telemetry and recent operational activity.';
    }

    if (tenantId) {
      return `Global overview across ${data.tenants.active} active tenants. Operational pages stay focused on the selected tenant.`;
    }

    return `${data.tenants.active} active tenants, ${data.logSummary.recentErrors} recent errors, ${data.systemTotals.reservationsPending} reservations pending.`;
  }, [data, tenantId]);

  return (
    <Box sx={{ py: 2 }}>
      <Stack spacing={2.5}>
        <Paper
          elevation={0}
          sx={{
            px: { xs: 2.25, md: 3 },
            py: 2.5,
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(23,32,51,0.98) 0%, rgba(31,42,68,0.94) 52%, rgba(57,41,29,0.92) 100%)',
            color: 'common.white',
          }}
        >
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between">
            <Box sx={{ maxWidth: 760 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label="Canonical 3100" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' }} />
                <Chip
                  label={tenantId ? 'Global overview' : 'Global context'}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' }}
                />
                {data ? (
                  <Chip
                    label={`Health: ${data.health.status}`}
                    size="small"
                    color={statusChipColor(data.health.status)}
                    sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' }}
                  />
                ) : null}
              </Stack>
              <Typography variant="overline" sx={{ display: 'block', mt: 2, color: 'rgba(255,255,255,0.72)', letterSpacing: '0.18em' }}>
                Superadmin operations
              </Typography>
              <Typography variant="h4" sx={{ mt: 0.75, maxWidth: 680 }}>
                Run the platform from a dense, canonical control surface.
              </Typography>
              <Typography sx={{ mt: 1.25, color: 'rgba(255,255,255,0.78)', maxWidth: 700 }}>
                {headlineMeta}
              </Typography>
            </Box>

            <Stack spacing={1.25} alignItems={{ xs: 'stretch', lg: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{ bgcolor: '#fff', color: '#172033', '&:hover': { bgcolor: '#f5efe6' } }}
              >
                Create tenant
              </Button>
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => setInviteOpen(true)}
                disabled={!tenantId}
                sx={{ borderColor: 'rgba(255,255,255,0.22)', color: '#fff' }}
              >
                Invite manager
              </Button>
              <Button component={Link} to="/dashboard/logs" variant="text" sx={{ color: '#fff' }}>
                Open logs workspace
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading && !data ? (
          <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={22} />
              <Typography>Loading canonical superadmin overview...</Typography>
            </Stack>
          </Paper>
        ) : null}

        {data ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              <MetricTile
                label="System health"
                value={data.health.status === 'degraded' ? 'Degraded' : 'Healthy'}
                meta={`${data.health.errorsLastHour} errors and ${data.health.warningsLastHour} warnings in the last hour.`}
                tone={data.health.status === 'degraded' ? 'error' : 'success'}
              />
              <MetricTile
                label="Tenants"
                value={`${data.tenants.active}/${data.tenants.total}`}
                meta={`${data.tenants.pendingInvites} pending invites waiting for activation.`}
                tone="default"
              />
              <MetricTile
                label="Reservations"
                value={`${data.systemTotals.reservationsActive}`}
                meta={`${data.systemTotals.reservationsPending} pending reservations across shared modules.`}
                tone={data.systemTotals.reservationsPending > 0 ? 'warning' : 'default'}
              />
              <MetricTile
                label="Log activity"
                value={`${data.logSummary.recentActivity}`}
                meta={`${data.logSummary.recentErrors} errors and ${data.logSummary.recentWarnings} warnings observed recently.`}
                tone={data.logSummary.recentErrors > 0 ? 'error' : 'default'}
              />
            </Box>

            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2}>
              <Paper elevation={0} sx={{ flex: 1, borderRadius: 4, border: '1px solid', borderColor: 'divider', p: 2.5 }}>
                <Typography variant="h6">Quick actions</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Jump directly into the surfaces that change system state.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                  <Button component={Link} to="/dashboard/tenants" variant="contained" startIcon={<ApartmentIcon />}>
                    Review tenants
                  </Button>
                  <Button component={Link} to="/dashboard/logs" variant="outlined" startIcon={<HistoryIcon />}>
                    Logs
                  </Button>
                  <Button component={Link} to="/dashboard/users" variant="outlined" startIcon={<PeopleOutlineIcon />}>
                    Users
                  </Button>
                  <Button component={Link} to="/dashboard/reports" variant="outlined" startIcon={<AssignmentTurnedInIcon />}>
                    Reports
                  </Button>
                  <Button component={Link} to="/dashboard/inventory-overview" variant="outlined" startIcon={<Inventory2Icon />}>
                    Inventory
                  </Button>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ flex: 1, borderRadius: 4, border: '1px solid', borderColor: 'divider', p: 2.5 }}>
                <Typography variant="h6">Shared totals</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Global platform volume across the shared data plane, independent of the focused tenant.
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                    gap: 1.5,
                  }}
                >
                  {[
                    ['Locations', data.systemTotals.locations],
                    ['Spaces', data.systemTotals.spaces],
                    ['Tables', data.systemTotals.tables],
                    ['Users', data.systemTotals.users],
                    ['Customers', data.systemTotals.customers],
                    ['Reservations', data.systemTotals.reservations],
                    ['Table reservations', data.systemTotals.tableReservations],
                    ['Pending invites', data.tenants.pendingInvites],
                  ].map(([label, value]) => (
                    <Box
                      key={String(label)}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: alpha('#172033', 0.02),
                      }}
                    >
                      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.14em' }}>
                        {label}
                      </Typography>
                      <Typography variant="h6">{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: 'repeat(3, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              <PreviewPanel title="Recent tenants" subtitle="Latest tenant records and module footprints." actionLabel="Open tenants" actionTo="/dashboard/tenants">
                <Stack divider={<Divider flexItem />} spacing={0}>
                  {data.recentTenants.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No tenants available.</Typography>
                  ) : data.recentTenants.map((tenant) => (
                    <Box key={tenant.id} sx={{ py: 1.25 }}>
                      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600 }}>{tenant.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {tenant.city || 'No city'} {tenant.subdomain ? `• ${tenant.subdomain}` : ''}
                          </Typography>
                        </Box>
                        <Chip label={tenant.status || 'Unknown'} size="small" color={statusChipColor(tenant.status)} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                        {tenant.enabledModules.length > 0 ? tenant.enabledModules.join(', ') : 'No modules enabled'} • {formatDate(tenant.createdAt)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </PreviewPanel>

              <PreviewPanel title="Pending invites" subtitle="Invitations that still need acceptance." actionLabel="Manage tenants" actionTo="/dashboard/tenants">
                <Stack divider={<Divider flexItem />} spacing={0}>
                  {data.pendingInvites.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No pending invites.</Typography>
                  ) : data.pendingInvites.map((invite) => (
                    <Box key={invite.id} sx={{ py: 1.25 }}>
                      <Typography sx={{ fontWeight: 600 }}>{invite.email}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        {invite.tenantName || 'No tenant attached'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                        Created {formatDate(invite.createdAt)} • Expires {formatDate(invite.expiresAt)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </PreviewPanel>

              <PreviewPanel title="Recent logs" subtitle="Newest events from the shared log stream." actionLabel="Open logs" actionTo="/dashboard/logs">
                <Stack divider={<Divider flexItem />} spacing={0}>
                  {data.recentLogs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No recent logs found.</Typography>
                  ) : data.recentLogs.map((entry) => (
                    <Box key={entry.id} sx={{ py: 1.25 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        <Chip
                          label={entry.level}
                          size="small"
                          color={statusChipColor(entry.level)}
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(entry.timestamp)}
                        </Typography>
                        {entry.component ? (
                          <Typography variant="caption" color="text.secondary">
                            {entry.component}
                          </Typography>
                        ) : null}
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 0.9 }}>
                        {entry.message}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </PreviewPanel>
            </Box>
          </>
        ) : null}
      </Stack>

      <CreateTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
          refresh();
        }}
      />

      {tenantId ? (
        <InviteDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          tenantId={tenantId}
          onSuccess={() => {
            setInviteOpen(false);
            refresh();
          }}
        />
      ) : null}
    </Box>
  );
};

export default Dashboard;
